/**
 * Procedural domain map view (mockups 3 + 4, dark-themed).
 *
 * Renders the comprehensive quest map as an SVG with:
 *   - A faint, deterministically-placed landscape backdrop
 *     (contour blobs + scattered dots + ridge curves).
 *   - One station per quest, positioned via the procedural
 *     layout algorithm; tapping a station opens a bottom drawer
 *     with the quest's detail (core concepts + milestones) and
 *     a "Start this quest" CTA.
 *   - Dashed-line connections between stations in the same
 *     category, plus thin links between category centers so the
 *     map reads as one continuous landscape.
 *
 * No AI viz — everything is deterministic.  The same quest set
 * always produces the same map; different quest sets produce
 * visibly different maps because positions + decorations are
 * seeded by quest IDs.
 */

import React, { useEffect, useMemo, useState } from "react";
import { css } from "@emotion/css";
import type {
  ArcQuestRecommendation,
  ArcQuestCategory,
  ArcMilestone,
  QuestCategoryAccent,
} from "../../types/arcGenerationSession";
import {
  buildProceduralMap,
  pathThroughPositions,
  type MapStation,
} from "../../data/proceduralMap";
import { questIconSvg } from "../../data/questIcons";
import {
  MOUNTAIN_TEMPLATES,
  ROCK_TEMPLATES,
  TREE_TEMPLATES,
  type MapShapeTemplate,
} from "../../data/mapTemplates";

// ── Theme helpers ───────────────────────────────────────────────────

function accentToColor(accent: QuestCategoryAccent | undefined): string {
  switch (accent) {
    case "primary": return "var(--color-primary)";
    case "success": return "var(--color-success-text)";
    case "warning": return "#eab308";
    case "accent": return "var(--color-loader-rendering)";
    case "neutral":
    default: return "var(--color-text-secondary)";
  }
}

// ── Styles ──────────────────────────────────────────────────────────

const mapShellStyle = css`
  position: relative;
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border-subtle);
  /* Aspect ratio comes from the procedural layout; SVG fills. */
`;

const svgStyle = css`
  display: block;
  width: 100%;
  height: auto;
`;

const drawerStyle = css`
  margin-top: 12px;
  padding: 16px 16px 18px;
  border-radius: 14px;
  background: var(--color-coach-bubble-bg);
  border: 1px solid var(--color-primary-border);
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const drawerTitleStyle = css`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.05em;
  font-weight: 700;
  color: var(--color-text);
  & > .num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: var(--color-primary);
    color: var(--color-text-inverse);
    font-size: 0.85em;
  }
`;

const drawerDescStyle = css`
  font-size: 0.9em;
  color: var(--color-text-secondary);
  line-height: 1.5;
`;

const drawerLabelStyle = css`
  font-size: 0.72em;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: var(--color-text-muted);
  text-transform: uppercase;
  margin-top: 4px;
`;

const drawerCtaStyle = css`
  width: 100%;
  padding: 12px 16px;
  border: none;
  border-radius: 12px;
  background: var(--color-primary);
  color: var(--color-text-inverse);
  font-size: 0.95em;
  font-weight: 700;
  cursor: pointer;
  margin-top: 6px;
  &:hover:not(:disabled) { filter: brightness(1.05); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

// ── Capability detail (Phase 6 / mockup 4) ──────────────────────────

const detailContainerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const detailHeaderStyle = css`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
`;

const detailBackBtnStyle = css`
  width: 36px;
  height: 36px;
  border-radius: 999px;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  color: var(--color-text);
  cursor: pointer;
  font-size: 1em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover { border-color: var(--color-primary); color: var(--color-primary); }
`;

const detailHeaderTextStyle = css`
  font-size: 0.78em;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-muted);
`;

const conceptsGridStyle = css`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-top: 4px;

  @media (max-width: 500px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const conceptCellStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 10px 6px;
  border-radius: 10px;
  background: var(--color-bg-subtle);
  border: 1px solid var(--color-border-subtle);
  & > .ic {
    width: 28px;
    height: 28px;
    color: var(--color-primary);
    & > svg { width: 100%; height: 100%; display: block; }
  }
  & > .label {
    font-size: 0.78em;
    font-weight: 600;
    color: var(--color-text);
    text-align: center;
    line-height: 1.3;
  }
`;


const buildsTowardCardStyle = css`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  cursor: pointer;
  font-size: 0.85em;
  color: var(--color-text);
  &:hover { border-color: var(--color-primary); color: var(--color-primary); }
  & > .ic {
    width: 24px;
    height: 24px;
    color: var(--color-primary);
    & > svg { width: 100%; height: 100%; display: block; }
  }
  & > .arrow { color: var(--color-text-muted); }
`;

const tipStyle = css`
  text-align: center;
  font-size: 0.85em;
  color: var(--color-text-muted);
  padding: 14px 16px;
`;

const legendStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 18px;
  padding: 12px 4px 2px;
  font-size: 0.82em;
  color: var(--color-text-secondary);
`;

const legendItemStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 7px;
`;

const legendDotStyle = css`
  display: inline-block;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1px solid var(--color-bg);
  box-shadow: 0 0 0 1px currentColor;
`;

// Station puck radii (viewbox units).  Shared with the procedural
// generator so islands are sized to clearly enclose the node.
const STATION_R = 30;
const SUBMAP_STATION_R = 32;

// ── Responsive metrics ──────────────────────────────────────────────

/**
 * Picks the map's viewbox aspect + coordinate width for the
 * current viewport.
 *
 * - Desktop / wide viewports keep the landscape layout untouched
 *   (the caller's ``desktopAspect``, full 1000-unit coord width).
 * - Mobile (≤ 700px wide) switches to a PORTRAIT aspect so the
 *   map fills ~85% of the viewport height instead of being a
 *   short letterboxed strip, and narrows the coordinate system so
 *   station pucks + labels render large enough to read and tap.
 */
interface MapMetrics {
  aspect: number;
  vw: number;
  // Font sizing for station + category labels, in viewbox units.
  // Bumped on mobile (paired with a smaller ``vw``) so labels stay
  // readable at the rendered scale, where each viewbox unit
  // becomes ~0.77 device pixels.
  font: number;
  lineH: number;
  /** Avg char width at ``font`` size, used to size label backing
   * pills.  Slightly pessimistic so the pill is never too narrow. */
  charW: number;
  /** Max chars per wrapped line — narrower on mobile so the wider
   * (bigger-font) labels still fit inside the per-station label
   * width cap and don't overflow their backing pills. */
  wrapChars: number;
}

function useMapMetrics(desktopAspect: number): MapMetrics {
  const [vp, setVp] = useState(() => ({
    w: typeof window !== "undefined" ? window.innerWidth : 1280,
    h: typeof window !== "undefined" ? window.innerHeight : 800,
  }));
  useEffect(() => {
    const onResize = () =>
      setVp({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  if (vp.w > 700) {
    return {
      aspect: desktopAspect,
      vw: 1000,
      font: 11,
      lineH: 13,
      charW: 7.5,
      wrapChars: 22,
    };
  }
  // Map shell width ≈ viewport minus the explore-overlay padding.
  const containerW = Math.max(240, vp.w - 36);
  const targetH = vp.h * 0.78;
  const aspect = Math.max(0.42, Math.min(1.2, containerW / targetH));
  // Mobile: narrower vw + bigger font sizes — together they bring
  // labels from ~7px rendered to ~10-11px (readable) without
  // shrinking the canvas.  ``wrapChars`` is reduced so the wider
  // (bigger-font) labels stay inside the per-station pill cap.
  return {
    aspect,
    vw: 460,
    font: 14,
    lineH: 17,
    charW: 9.5,
    wrapChars: 13,
  };
}

// ── Component ───────────────────────────────────────────────────────

interface QuestMapProps {
  quests: ArcQuestRecommendation[];
  categories: ArcQuestCategory[];
  domainName?: string;
  // Clicking a station drills into the capability detail.  The
  // drill-in state is owned by the parent (``ExploreQuests``) so
  // the list view and the map view share one detail screen.
  onDrillIn: (questId: string) => void;
}

export const QuestMap: React.FC<QuestMapProps> = ({
  quests,
  categories,
  domainName,
  onDrillIn,
}) => {
  const accentByCategory = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of categories) m.set(c.categoryId, accentToColor(c.accentColor));
    return m;
  }, [categories]);

  const stations: MapStation[] = useMemo(
    () => quests.map((q, idx) => ({
      id: q.questId,
      categoryId: q.categoryId || "uncategorized",
      order: idx,
    })),
    [quests],
  );

  // Seed includes the domain name so two domains with the same
  // quest IDs would still lay out differently.
  const seed = useMemo(
    () => (domainName || "domain") + "|" + quests.length,
    [domainName, quests.length],
  );

  // Mobile gets a portrait layout that fills the tall viewport;
  // desktop keeps the landscape (1.5) map unchanged.
  const { aspect: targetAspect, vw, font: labelFont, lineH: labelLineH, charW: labelCharW, wrapChars: labelWrapChars } = useMapMetrics(1.5);

  const map = useMemo(
    () =>
      buildProceduralMap(stations, {
        seed,
        connectByCategory: true,
        aspect: targetAspect,
        viewW: vw,
        nodeRadius: STATION_R,
      }),
    [stations, seed, targetAspect, vw],
  );

  // Position lookup.
  const posById = useMemo(() => {
    const m = new Map<string, { x: number; y: number; categoryId: string }>();
    for (const p of map.positions) m.set(p.id, p);
    return m;
  }, [map]);

  // SVG viewbox dimensions.  ``vw`` is the coordinate-system width
  // — narrower on mobile so fixed-size elements (station pucks,
  // labels) render large enough to read and tap.  Height follows
  // the layout aspect.
  const VW = vw;
  const VH = Math.round(VW / map.aspect);

  if (quests.length === 0) {
    return <div className={tipStyle}>No quests to map yet.</div>;
  }

  return (
    <div data-testid="quest-map">
      <div className={mapShellStyle}>
        <svg
          className={svgStyle}
          viewBox={`0 0 ${VW} ${VH}`}
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMidYMid meet"
          aria-label="Procedural quest map"
        >
          <defs>
            {/* Subtle vertical sky tint at the top */}
            <linearGradient id="qm-sky" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--color-bg-subtle)" />
              <stop offset="100%" stopColor="var(--color-bg-elevated)" />
            </linearGradient>
            {/* Soft halo for the active/selected station */}
            <radialGradient id="qm-halo" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0%" stopColor="currentColor" stopOpacity="0.35" />
              <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Layer 1: sky-tinted background */}
          <rect x={0} y={0} width={VW} height={VH} fill="url(#qm-sky)" />

          {/* Layer 2: distant mountains.  Each instance picks a
              curated template from ``mapTemplates`` (sharp peak,
              rounded peak, snow-cap, two-peak range, etc.).
              Sorted back-to-front for parallax depth. */}
          {map.mountains
            .slice()
            .sort((a, b) => a.depth - b.depth)
            .map((m, i) => {
              const tpl = MOUNTAIN_TEMPLATES[m.templateIdx];
              if (!tpl) return null;
              const widthPx = m.width * VW;
              const heightPx = widthPx / tpl.aspect;
              return (
                <TemplateShape
                  key={`mt${i}`}
                  template={tpl}
                  anchorX={m.cx * VW}
                  // Anchor on the horizon line; baseline of the
                  // PNG lands at (cx * VW, cy * VH + heightPx/3)
                  // so the mountain's foot sits near the cy line
                  // and the peak rises above it.
                  anchorY={m.cy * VH + heightPx * 0.35}
                  width={widthPx}
                  flipX={m.flipX}
                  // Further mountains render lighter for parallax.
                  opacity={0.5 + m.depth * 0.18}
                />
              );
            })}

          {/* Layer 4: rock clusters — PNG templates from
              ``ROCK_TEMPLATES`` scattered between trees.  Skipped
              entirely until at least one ``rock_*.png`` is dropped
              into ``data/map_assets/``. */}
          {ROCK_TEMPLATES.length > 0 && map.rocks.map((r, i) => {
            const tpl = ROCK_TEMPLATES[r.templateIdx % ROCK_TEMPLATES.length];
            return (
              <TemplateShape
                key={`rk${i}`}
                template={tpl}
                anchorX={r.cx * VW}
                anchorY={r.cy * VH}
                width={r.width * VW}
                flipX={r.flipX}
                opacity={0.85}
              />
            );
          })}

          {/* Layer 5: pine trees — PNG templates from
              ``TREE_TEMPLATES``.  Skipped entirely until at least
              one ``tree_*.png`` is dropped into
              ``data/map_assets/``. */}
          {TREE_TEMPLATES.length > 0 && map.trees.map((t, i) => {
            const tpl = TREE_TEMPLATES[t.templateIdx % TREE_TEMPLATES.length];
            return (
              <TemplateShape
                key={`tr${i}`}
                template={tpl}
                anchorX={t.cx * VW}
                anchorY={t.cy * VH}
                width={t.width * VW}
                flipX={t.flipX}
                opacity={0.92}
              />
            );
          })}

          {/* Layer 7: islands with a slight 3D / isometric feel
              — a darker shadow blob underneath + a lighter top
              surface make the island appear to float above the
              landscape rather than sit flat on it. */}
          {map.islands.map((isl) => {
            const station = posById.get(isl.stationId);
            const accent = station
              ? accentByCategory.get(station.categoryId) || "var(--color-text-secondary)"
              : "var(--color-text-secondary)";
            return (
              <g key={`isl-${isl.stationId}`}>
                {/* Soft category-tinted halo so the island
                    catches light in its category's color. */}
                <g style={{ color: accent }}>
                  <path
                    d={scalePath(isl.shadowPath, VW, VH)}
                    fill="url(#qm-halo)"
                    opacity={0.55}
                  />
                </g>
                {/* "Side" of the island — same shape, offset
                    down-right, slightly darker.  Peeks out at
                    the bottom edge of the top surface for an
                    isometric depth cue. */}
                <g transform={`translate(3, 10)`}>
                  <path
                    d={scalePath(isl.path, VW, VH)}
                    fill="var(--color-bg-subtle)"
                  />
                </g>
                {/* Top surface of the island — lighter tint,
                    with a thin category-colored rim. */}
                <path
                  d={scalePath(isl.path, VW, VH)}
                  fill="var(--color-bg-raised)"
                  stroke={accent}
                  strokeWidth={1.3}
                  strokeOpacity={0.65}
                />
              </g>
            );
          })}

          {/* Layer 7: curvy dashed trails between stations.  The
              path meanders perpendicular to A→B with a
              deterministic wobble — playful, not straight. */}
          {map.edges.map((e, i) => {
            const a = posById.get(e.from);
            const b = posById.get(e.to);
            if (!a || !b) return null;
            const ax = a.x * VW;
            const ay = a.y * VH;
            const bx = b.x * VW;
            const by = b.y * VH;
            return (
              <path
                key={`e${i}`}
                d={curvyTrail(ax, ay, bx, by, i)}
                fill="none"
                stroke="var(--color-text-muted)"
                strokeWidth={1.6}
                strokeDasharray="4 6"
                opacity={0.7}
              />
            );
          })}

          {/* Layer 7b: per-category labels — small uppercase
              text near the top of each cluster.  Wraps onto two
              lines for long names so the label is fully visible
              even on narrow viewports. */}
          {map.categoryClusters.map((c) => {
            const category = categories.find((cat) => cat.categoryId === c.categoryId);
            if (!category) return null;
            const accent = accentByCategory.get(c.categoryId) || "var(--color-text-secondary)";
            // Wrap long category names to keep the pill narrow
            // enough to fit when the cluster sits near the
            // viewbox edge.  ~22 chars per line accommodates
            // most names without splitting cleanly readable
            // phrases.
            // Category labels stay at 2 lines max — they're short
            // enough that 3 lines just adds vertical noise into
            // the cluster.
            const lines = wrapLabel(
              category.categoryName.toUpperCase(),
              Math.max(12, labelWrapChars + 4),
              2,
            );
            const longestLen = Math.max(...lines.map((l) => l.length));
            const labelW = Math.min(VW * 0.44, longestLen * labelCharW + 20);
            const labelH = lines.length * labelLineH + 8;
            // Place the label clear of the cluster's STATIONS so
            // it never sits on top of an island/node.  Anchor off
            // the actual topmost / bottommost node of the category
            // (not the y-equivalent cluster radius, which mixes in
            // the x-spread): above the top node, or below the
            // its stations.  Always ABOVE the cluster's topmost
            // node — clamped upward only if the ideal position
            // would push it off the top edge of the canvas (the
            // layout's ``longLo`` clamp on positions guarantees
            // enough headroom for this in normal cases).
            const catNodeYs = map.positions
              .filter((p) => p.categoryId === c.categoryId)
              .map((p) => p.y);
            const topNodeY = (catNodeYs.length ? Math.min(...catNodeYs) : c.cy) * VH;
            const idealAboveY = topNodeY - STATION_R - 12 - labelH / 2;
            const cyCenter = Math.max(labelH / 2 + 4, idealAboveY);
            const cx = Math.max(
              labelW / 2 + 4,
              Math.min(VW - labelW / 2 - 4, c.cx * VW),
            );
            const textTopY = cyCenter - (lines.length - 1) * labelLineH / 2;
            return (
              <g
                key={`cl-${c.categoryId}`}
                style={{ pointerEvents: "none" }}
              >
                <rect
                  x={cx - labelW / 2}
                  y={cyCenter - labelH / 2}
                  width={labelW}
                  height={labelH}
                  rx={labelH / 2}
                  fill="var(--color-bg)"
                  stroke={accent}
                  strokeOpacity={0.5}
                  opacity={0.94}
                />
                <text
                  x={cx}
                  y={textTopY}
                  fontSize={labelFont - 1}
                  fontWeight="700"
                  letterSpacing="0.06em"
                  fill={accent}
                  textAnchor="middle"
                  dominantBaseline="central"
                >
                  {lines.map((line, li) => (
                    <tspan key={li} x={cx} dy={li === 0 ? 0 : labelLineH}>
                      {line}
                    </tspan>
                  ))}
                </text>
              </g>
            );
          })}

          {/* Layer 8: stations (icon + numbered badge + label).
              Wrapped in an IIFE so we can precompute the
              "topmost-of-category" map once and use it inside the
              quests.map to decide whether to flip a label above
              (the topmost station's label always stays below — the
              category label lives just above it). */}
          {(() => {
            const topmostByCategory = new Map<string, string>();
            for (const q2 of quests) {
              const p2 = posById.get(q2.questId);
              if (!p2) continue;
              const cur = topmostByCategory.get(p2.categoryId);
              const curP = cur ? posById.get(cur) : null;
              if (!curP || p2.y < curP.y) {
                topmostByCategory.set(p2.categoryId, q2.questId);
              }
            }
            return quests.map((q) => {
              const p = posById.get(q.questId);
              if (!p) return null;
              const cx = p.x * VW;
              const cy = p.y * VH;
              const accent = accentByCategory.get(p.categoryId) || "var(--color-text-secondary)";
              const isBest = q.recommendationTier === "best_fit";
              const r = STATION_R;
              const lines = wrapLabel(q.title, labelWrapChars);
              const labelW = Math.min(
                VW * 0.34,
                Math.max(...lines.map((l) => l.length)) * labelCharW + 16,
              );
              const labelH = lines.length * labelLineH + 6;
              // Smart above-or-below placement: count other
              // stations whose puck would sit inside the label's
              // bounding box on each side, pick the clearer.
              // Default below.  The TOPMOST station of a category
              // never flips above — that's where the category
              // label lives.
              const isTopOfCategory =
                topmostByCategory.get(p.categoryId) === q.questId;
              const countNearLabel = (yMin: number, yMax: number) => {
                let n = 0;
                for (const q2 of quests) {
                  if (q2.questId === q.questId) continue;
                  const p2 = posById.get(q2.questId);
                  if (!p2) continue;
                  const cx2 = p2.x * VW;
                  const cy2 = p2.y * VH;
                  if (
                    Math.abs(cx2 - cx) < labelW / 2 + r &&
                    cy2 > yMin - r &&
                    cy2 < yMax + r
                  ) {
                    n++;
                  }
                }
                return n;
              };
              const belowYMin = cy + r;
              const belowYMax = cy + r + 12 + labelH;
              const aboveYMin = cy - r - 12 - labelH;
              const aboveYMax = cy - r;
              const placeAbove =
                !isTopOfCategory &&
                countNearLabel(belowYMin, belowYMax) >
                  countNearLabel(aboveYMin, aboveYMax);
              const labelStartY = placeAbove
                ? cy - r - 14 - (lines.length - 1) * labelLineH
                : cy + r + 12;
            return (
              <g
                key={q.questId}
                style={{ cursor: "pointer" }}
                onClick={() => onDrillIn(q.questId)}
                data-testid={`map-station-${q.questId}`}
              >
                {/* Station puck */}
                <circle
                  cx={cx}
                  cy={cy}
                  r={r}
                  fill="var(--color-bg)"
                  stroke={accent}
                  strokeWidth={2.4}
                />
                {/* Quest icon */}
                <foreignObject
                  x={cx - 17}
                  y={cy - 17}
                  width={34}
                  height={34}
                  style={{ pointerEvents: "none" }}
                >
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      color: accent,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    dangerouslySetInnerHTML={{ __html: questIconSvg(q.iconKey) }}
                  />
                </foreignObject>

                {/* Best-fit marker — a ★ badge flags the
                    recommended starting capability.  Non-hero
                    stations carry no number badge (the high-level
                    map reads cleaner without them; sequence
                    numbers live on the capability-detail map). */}
                {isBest && (
                  <>
                    <circle
                      cx={cx + r * 0.78}
                      cy={cy - r * 0.78}
                      r={11}
                      fill={accent}
                      stroke="var(--color-bg)"
                      strokeWidth={1.5}
                    />
                    <text
                      x={cx + r * 0.78}
                      y={cy - r * 0.78}
                      fontSize="11"
                      fontWeight="700"
                      fill="var(--color-text-inverse)"
                      textAnchor="middle"
                      dominantBaseline="central"
                      style={{ pointerEvents: "none" }}
                    >
                      ★
                    </text>
                  </>
                )}

                {/* Label backing pill — keeps long labels legible
                    even if they overlap a tree / dot decoration */}
                <rect
                  x={cx - labelW / 2}
                  y={labelStartY - labelLineH + 2}
                  width={labelW}
                  height={lines.length * labelLineH + 6}
                  rx={6}
                  fill="var(--color-bg)"
                  opacity={0.78}
                />
                <text
                  x={cx}
                  y={labelStartY}
                  fontSize={labelFont}
                  fontWeight="600"
                  fill={accent}
                  textAnchor="middle"
                  style={{ pointerEvents: "none" }}
                >
                  {lines.map((line, li) => (
                    <tspan
                      key={li}
                      x={cx}
                      dy={li === 0 ? 0 : labelLineH}
                    >
                      {line}
                    </tspan>
                  ))}
                </text>
              </g>
            );
            });
          })()}
        </svg>
      </div>

      {/* Category legend — color → category name. Same-color
          islands ARE in the same category; the legend makes
          that mapping explicit. */}
      {categories.length > 0 && (
        <div className={legendStyle} data-testid="quest-map-legend">
          {categories.map((c) => (
            <span
              key={c.categoryId}
              className={legendItemStyle}
              data-testid={`map-legend-${c.categoryId}`}
            >
              <span
                className={legendDotStyle}
                style={{ background: accentToColor(c.accentColor) }}
              />
              {c.categoryName}
            </span>
          ))}
        </div>
      )}

    </div>
  );
};

/**
 * Greedy word-wrap a label into at most 2 lines, each no longer
 * than ``maxChars``.  Used for the station label tspans on the
 * map — keeps the full title readable even when it's long.
 */
function wrapLabel(
  text: string,
  maxChars: number,
  maxLines: number = 3,
): string[] {
  if (text.length <= maxChars) return [text];
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let cur = "";
  for (const w of words) {
    const next = cur ? cur + " " + w : w;
    if (next.length > maxChars && cur) {
      lines.push(cur);
      cur = w;
    } else {
      cur = next;
    }
  }
  if (cur) lines.push(cur);
  if (lines.length <= maxLines) return lines;
  // Merge overflow into the LAST allowed line.  Only add an
  // ellipsis if we're actually cutting characters off — a
  // perfectly-fitting last line shouldn't get a spurious "…".
  const lastIdx = maxLines - 1;
  const merged = lines[lastIdx] + " " + lines.slice(maxLines).join(" ");
  const cap = maxChars + 4;
  const last = merged.length > cap ? merged.slice(0, cap) + "…" : merged;
  return [...lines.slice(0, lastIdx), last];
}

/**
 * Scale an SVG path string from normalized [0,1] coords to the
 * given viewbox dimensions.  The path strings emitted by
 * ``proceduralMap.ts`` use normalized coordinates; the renderer
 * scales them at draw time so the same algorithm works for any
 * viewbox size.
 */
/**
 * Render a curated PNG template (from ``mapTemplates``) at a
 * given anchor on the map, scaled to ``width`` viewbox-pixels.
 * The image's center-bottom lands at (anchorX, anchorY) so the
 * asset appears to sit on the ground at the chosen position.
 *
 * Height is derived from ``template.aspect`` so the PNG never
 * stretches.  Optional horizontal flip adds variety without
 * extra templates.
 */
const TemplateShape: React.FC<{
  template: MapShapeTemplate;
  anchorX: number;
  anchorY: number;
  width: number;
  flipX?: boolean;
  opacity?: number;
}> = ({ template, anchorX, anchorY, width, flipX = false, opacity = 1 }) => {
  const height = width / template.aspect;
  const x = anchorX - width / 2;
  const y = anchorY - height;
  // Flip around the image's horizontal center.
  const transform = flipX
    ? `translate(${anchorX} 0) scale(-1 1) translate(${-anchorX} 0)`
    : undefined;
  return (
    <image
      href={template.imageUrl}
      x={x}
      y={y}
      width={width}
      height={height}
      opacity={opacity}
      transform={transform}
      preserveAspectRatio="xMidYMid meet"
    />
  );
};

function scalePath(d: string, vw: number, vh: number): string {
  // Replace each "x y" pair with scaled values.  Commands
  // (MmLlCcQqZzTtSsHhVv) stay as-is.
  return d.replace(
    /(-?\d+\.?\d*)\s+(-?\d+\.?\d*)/g,
    (_, x, y) => `${(parseFloat(x) * vw).toFixed(2)} ${(parseFloat(y) * vh).toFixed(2)}`,
  );
}

/**
 * Smooth open Catmull-Rom path through the given points, encoded
 * as a series of cubic-Bezier segments.  Used by ``curvyTrail``
 * to draw a flowing line through A → waypoints → B.
 */
function catmullRomPath(pts: ReadonlyArray<readonly [number, number]>): string {
  const n = pts.length;
  if (n < 2) return "";
  const P = (i: number) => pts[Math.max(0, Math.min(n - 1, i))];
  let d = `M ${pts[0][0].toFixed(2)} ${pts[0][1].toFixed(2)}`;
  for (let i = 1; i < n; i++) {
    const p0 = P(i - 2);
    const p1 = P(i - 1);
    const p2 = P(i);
    const p3 = P(i + 1);
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d +=
      ` C ${c1x.toFixed(2)} ${c1y.toFixed(2)}` +
      ` ${c2x.toFixed(2)} ${c2y.toFixed(2)}` +
      ` ${p2[0].toFixed(2)} ${p2[1].toFixed(2)}`;
  }
  return d;
}

/**
 * Build a curvy, "trail-on-a-map" path between two stations.
 * Places 3 waypoints perpendicular to the A→B direction with a
 * deterministic wobble (per-edge index) and draws a smooth
 * Catmull-Rom spline through them, so the line meanders rather
 * than going straight.
 */
function curvyTrail(
  ax: number, ay: number, bx: number, by: number, seedIdx: number,
): string {
  const dx = bx - ax;
  const dy = by - ay;
  const L = Math.hypot(dx, dy);
  if (L < 6) {
    return `M ${ax.toFixed(2)} ${ay.toFixed(2)} L ${bx.toFixed(2)} ${by.toFixed(2)}`;
  }
  // Perpendicular unit vector (rotate (dx,dy) by 90°).
  const px = -dy / L;
  const py = dx / L;
  // Deterministic [0,1) wobble from seedIdx + waypoint index.
  const u = (k: number) => 0.5 + 0.5 * Math.sin(seedIdx * 1.7 + k * 5.13);
  const waves = 3;
  // Amplitude scales with edge length so short same-category
  // links curve gently while long inter-category trails sweep
  // more boldly.  Capped so very long edges don't go wild.
  const baseAmp = Math.min(L * 0.14, 40);
  const pts: Array<[number, number]> = [[ax, ay]];
  for (let i = 1; i <= waves; i++) {
    const t = i / (waves + 1);
    const wx = ax + dx * t;
    const wy = ay + dy * t;
    const wobble = 0.55 + 0.55 * u(i);
    const sign = i % 2 === 0 ? -1 : 1;
    pts.push([wx + px * baseAmp * wobble * sign, wy + py * baseAmp * wobble * sign]);
  }
  pts.push([bx, by]);
  return catmullRomPath(pts);
}

// ── Capability detail (mockup 4) ────────────────────────────────────

interface CapabilityDetailProps {
  quest: ArcQuestRecommendation;
  milestones: ArcMilestone[];
  allQuests: ArcQuestRecommendation[];
  // Category accent color of the parent quest — drives the
  // tint of the milestone stations, river, halo and labels so
  // the detail screen reads as a focused view of the same
  // category territory the learner just tapped on the high-level
  // map.  CSS string (e.g. ``var(--color-primary)`` or ``#eab308``).
  accent: string;
  onBack: () => void;
  onChoose: () => void;
  onJumpToQuest: (questId: string) => void;
  isStreaming: boolean;
}

export const CapabilityDetail: React.FC<CapabilityDetailProps> = ({
  quest,
  milestones,
  allQuests,
  accent,
  onBack,
  onChoose,
  onJumpToQuest,
  isStreaming,
}) => {
  // Procedural milestone sub-map: connect sequentially so the
  // shape reads as a linear path, with the same backdrop style
  // as the domain map.
  const stations: MapStation[] = useMemo(
    () => milestones.map((m, idx) => ({
      id: m.milestoneId,
      categoryId: "milestones",
      order: idx,
    })),
    [milestones],
  );
  // Desktop: a wide 2.2:1 strip for the milestone chain.  Mobile:
  // a portrait layout that fills the tall viewport (the chain then
  // runs top-to-bottom instead of left-to-right).
  const { aspect: targetAspect, vw, font: labelFont, lineH: labelLineH, charW: labelCharW, wrapChars: labelWrapChars } = useMapMetrics(2.2);
  const subMap = useMemo(
    () => buildProceduralMap(stations, {
      seed: quest.questId + "|milestones",
      connectByCategory: false,
      connectSequential: true,
      aspect: targetAspect,
      viewW: vw,
      nodeRadius: SUBMAP_STATION_R,
    }),
    [stations, quest.questId, targetAspect, vw],
  );
  const posById = useMemo(() => {
    const m = new Map<string, { x: number; y: number }>();
    for (const p of subMap.positions) m.set(p.id, p);
    return m;
  }, [subMap]);

  const VW = vw;
  const VH = Math.round(VW / subMap.aspect);

  // Core concept chips with icons — use the first milestone's
  // iconKey as a hint when there's no per-concept icon mapping.
  const concepts = (quest.coreConcepts || "")
    .split(/[,;\n]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 4);
  // Map concept name → milestone iconKey when one matches by
  // string contains, else fall back to the quest's iconKey.
  const conceptIconFor = (concept: string): string => {
    const cl = concept.toLowerCase();
    for (const m of milestones) {
      if (m.concepts.toLowerCase().includes(cl)) return m.iconKey;
    }
    return quest.iconKey || "generic";
  };

  // "Builds toward" — surface up to 2 alternative or good_next
  // quests so the learner sees the through-line.  Skip this
  // quest itself.
  const buildsToward = allQuests
    .filter(
      (q) =>
        q.questId !== quest.questId &&
        (q.recommendationTier === "good_next" ||
          q.recommendationTier === "alternative"),
    )
    .slice(0, 2);

  return (
    <div
      className={detailContainerStyle}
      data-testid={`capability-detail-${quest.questId}`}
    >
      <div className={detailHeaderStyle}>
        <button
          type="button"
          className={detailBackBtnStyle}
          onClick={onBack}
          aria-label="Back to map"
          data-testid="capability-detail-back"
        >
          ←
        </button>
        <span className={detailHeaderTextStyle}>Capability detail</span>
      </div>

      {/* Procedural milestone sub-map — same landscape style as
          the domain map but tuned for sequential reads (river
          follows the milestone chain, concept pills float along
          the river between stations).  Pure primary-color
          palette here since milestones don't have categories. */}
      <div className={mapShellStyle} data-testid="capability-detail-map">
        <svg
          className={svgStyle}
          viewBox={`0 0 ${VW} ${VH}`}
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMidYMid meet"
          aria-label="Milestone sub-map"
        >
          <defs>
            <linearGradient id="cd-sky" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--color-bg-subtle)" />
              <stop offset="100%" stopColor="var(--color-bg-elevated)" />
            </linearGradient>
            <radialGradient id="cd-halo" cx="0.5" cy="0.5" r="0.5">
              {/* Stops use ``currentColor`` so the parent ``<g>``'s
                  ``color`` (= category accent) drives the tint. */}
              <stop offset="0%" stopColor="currentColor" stopOpacity="0.32" />
              <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
            </radialGradient>
          </defs>

          <rect x={0} y={0} width={VW} height={VH} fill="url(#cd-sky)" />

          {/* Distant mountain layers */}
          {MOUNTAIN_TEMPLATES.length > 0 && subMap.mountains
            .slice()
            .sort((a, b) => a.depth - b.depth)
            .map((m, i) => {
              const tpl = MOUNTAIN_TEMPLATES[m.templateIdx % MOUNTAIN_TEMPLATES.length];
              const widthPx = m.width * VW;
              const heightPx = widthPx / tpl.aspect;
              return (
                <TemplateShape
                  key={`mt${i}`}
                  template={tpl}
                  anchorX={m.cx * VW}
                  anchorY={m.cy * VH + heightPx * 0.35}
                  width={widthPx}
                  flipX={m.flipX}
                  opacity={0.5 + m.depth * 0.18}
                />
              );
            })}

          {/* River = the path connecting the milestones in order
              (NOT a separate meander).  The river goes ONLY from
              the first milestone to the last — it does not stretch
              beyond the endpoints. */}
          {(() => {
            const orderedPts = milestones
              .map((m) => posById.get(m.milestoneId))
              .filter((p): p is { x: number; y: number } => !!p);
            if (orderedPts.length < 2) return null;
            const pathD = scalePath(pathThroughPositions(orderedPts), VW, VH);
            return (
              <>
                <path
                  d={pathD}
                  fill="none"
                  stroke={accent}
                  strokeWidth={Math.max(16, VW * 0.022)}
                  strokeOpacity={0.18}
                  strokeLinecap="round"
                />
                <path
                  d={pathD}
                  fill="none"
                  stroke={accent}
                  strokeWidth={Math.max(2.5, VW * 0.0035)}
                  strokeOpacity={0.5}
                  strokeLinecap="round"
                  strokeDasharray="4 8"
                />
              </>
            );
          })()}

          {/* Rocks + trees backdrop */}
          {ROCK_TEMPLATES.length > 0 && subMap.rocks.map((r, i) => {
            const tpl = ROCK_TEMPLATES[r.templateIdx % ROCK_TEMPLATES.length];
            return (
              <TemplateShape
                key={`rk${i}`}
                template={tpl}
                anchorX={r.cx * VW}
                anchorY={r.cy * VH}
                width={r.width * VW}
                flipX={r.flipX}
                opacity={0.85}
              />
            );
          })}
          {TREE_TEMPLATES.length > 0 && subMap.trees.map((t, i) => {
            const tpl = TREE_TEMPLATES[t.templateIdx % TREE_TEMPLATES.length];
            return (
              <TemplateShape
                key={`tr${i}`}
                template={tpl}
                anchorX={t.cx * VW}
                anchorY={t.cy * VH}
                width={t.width * VW}
                flipX={t.flipX}
                opacity={0.92}
              />
            );
          })}

          {/* Concept pills floating along the path, between
              consecutive milestones — surfaces what the learner
              will touch in plain language without crowding the
              stations. */}
          {milestones.length >= 2 && (() => {
            const pills: React.ReactNode[] = [];
            // Pull one concept per gap from the FIRST concept of
            // the LATER milestone in each pair, so the pill names
            // what's coming up next.
            //
            // Pill placement: perpendicular to the river segment,
            // offset far enough to clear the station-label boxes
            // on either end.  Sides ALTERNATE per gap so multiple
            // pills don't pile up on one bank — but if a chosen
            // side would push off-canvas, we flip to the other.
            const stationLabelHalf =
              Math.min(VW * 0.4, 22 * labelCharW + 16) / 2;
            for (let i = 1; i < milestones.length; i++) {
              const m0 = posById.get(milestones[i - 1].milestoneId);
              const m1 = posById.get(milestones[i].milestoneId);
              if (!m0 || !m1) continue;
              const concept = (milestones[i].concepts || "")
                .split(/[,;\n]/)
                .map((s) => s.trim())
                .find(Boolean);
              if (!concept) continue;
              const m0x = m0.x * VW;
              const m0y = m0.y * VH;
              const m1x = m1.x * VW;
              const m1y = m1.y * VH;
              const midX = (m0x + m1x) / 2;
              const midY = (m0y + m1y) / 2;
              const segDx = m1x - m0x;
              const segDy = m1y - m0y;
              const segLen = Math.hypot(segDx, segDy) || 1;
              // Perpendicular unit (rotate 90°).
              const perpX = -segDy / segLen;
              const perpY = segDx / segLen;
              const labelWidth = Math.max(
                60,
                Math.min(VW * 0.22, concept.length * labelCharW + 14),
              );
              // Clear the station labels: half station label + half
              // pill + a small gap.
              const offset = stationLabelHalf + labelWidth / 2 + 10;
              // Alternate side per pill.  Falls back to the
              // opposite side if the chosen one would push the
              // pill off-canvas.
              const inBounds = (cx: number, cy: number) =>
                cx >= labelWidth / 2 + 4 &&
                cx <= VW - labelWidth / 2 - 4 &&
                cy >= 16 &&
                cy <= VH - 16;
              const side = i % 2 === 0 ? 1 : -1;
              let pillCx = midX + perpX * offset * side;
              let pillCy = midY + perpY * offset * side;
              if (!inBounds(pillCx, pillCy)) {
                pillCx = midX + perpX * offset * -side;
                pillCy = midY + perpY * offset * -side;
              }
              // Final clamp so the pill never escapes the canvas.
              pillCx = Math.max(
                labelWidth / 2 + 4,
                Math.min(VW - labelWidth / 2 - 4, pillCx),
              );
              pillCy = Math.max(16, Math.min(VH - 16, pillCy));
              pills.push(
                <g key={`cp${i}`} opacity={0.95}>
                  <rect
                    x={pillCx - labelWidth / 2}
                    y={pillCy - 13}
                    width={labelWidth}
                    height={22}
                    rx={11}
                    fill={accent}
                    fillOpacity={0.16}
                    stroke={accent}
                    strokeOpacity={0.55}
                    strokeWidth={1}
                  />
                  <text
                    x={pillCx}
                    y={pillCy}
                    fontSize={labelFont}
                    fontWeight="600"
                    fill={accent}
                    textAnchor="middle"
                    dominantBaseline="central"
                  >
                    {concept}
                  </text>
                </g>,
              );
            }
            return pills;
          })()}

          {/* Milestone stations */}
          {milestones.map((m, idx) => {
            const p = posById.get(m.milestoneId);
            if (!p) return null;
            const cx = p.x * VW;
            const cy = p.y * VH;
            const r = SUBMAP_STATION_R;
            return (
              <g key={m.milestoneId}>
                {/* Halo — wrapped in a ``<g>`` whose ``color``
                    drives ``currentColor`` inside the gradient
                    stops, so the halo tints with the category. */}
                <g style={{ color: accent }}>
                  <circle
                    cx={cx}
                    cy={cy}
                    r={r + 14}
                    fill="url(#cd-halo)"
                  />
                </g>
                {/* Station puck */}
                <circle
                  cx={cx}
                  cy={cy}
                  r={r}
                  fill="var(--color-bg)"
                  stroke={accent}
                  strokeWidth={2.2}
                />
                <foreignObject
                  x={cx - 17}
                  y={cy - 17}
                  width={34}
                  height={34}
                  style={{ pointerEvents: "none" }}
                >
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      color: { accent } as any,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    dangerouslySetInnerHTML={{ __html: questIconSvg(m.iconKey) }}
                  />
                </foreignObject>
                <circle
                  cx={cx + r * 0.78}
                  cy={cy - r * 0.78}
                  r={11}
                  fill={accent}
                  stroke="var(--color-bg)"
                  strokeWidth={1.5}
                />
                <text
                  x={cx + r * 0.78}
                  y={cy - r * 0.78}
                  fontSize="11"
                  fontWeight="700"
                  fill="var(--color-text-inverse)"
                  textAnchor="middle"
                  dominantBaseline="central"
                >
                  {idx + 1}
                </text>
                {(() => {
                  const lines = wrapLabel(m.title, labelWrapChars);
                  const labelW = Math.min(
                    VW * 0.4,
                    Math.max(...lines.map((l) => l.length)) * labelCharW + 16,
                  );
                  const startY = cy + r + 12;
                  return (
                    <>
                      <rect
                        x={cx - labelW / 2}
                        y={startY - labelLineH + 2}
                        width={labelW}
                        height={lines.length * labelLineH + 6}
                        rx={6}
                        fill="var(--color-bg)"
                        opacity={0.82}
                      />
                      <text
                        x={cx}
                        y={startY}
                        fontSize={labelFont}
                        fontWeight="600"
                        fill={accent}
                        textAnchor="middle"
                      >
                        {lines.map((line, li) => (
                          <tspan
                            key={li}
                            x={cx}
                            dy={li === 0 ? 0 : labelLineH}
                          >
                            {line}
                          </tspan>
                        ))}
                      </text>
                    </>
                  );
                })()}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Bottom detail panel */}
      <div className={drawerStyle}>
        <div className={drawerTitleStyle}>
          <span className="num">
            {quest.recommendationTier === "best_fit" ? "★" : "•"}
          </span>
          {quest.title}
        </div>
        <div className={drawerDescStyle}>
          {(quest.whyItMatters || "")
            .replace(/\*\*([^*]+)\*\*/g, "$1")
            .split(/(?<=[.!?])\s/).slice(0, 2).join(" ")}
        </div>

        {concepts.length > 0 && (
          <>
            <div className={drawerLabelStyle}>Core concepts</div>
            <div className={conceptsGridStyle}>
              {concepts.map((c) => (
                <div key={c} className={conceptCellStyle}>
                  <span
                    className="ic"
                    aria-hidden="true"
                    dangerouslySetInnerHTML={{
                      __html: questIconSvg(conceptIconFor(c)),
                    }}
                  />
                  <span className="label">{c}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* The per-milestone list lived here; it's now omitted —
            the milestones are already shown on the capability
            sub-map above, so repeating them below is redundant. */}

        {buildsToward.length > 0 && (
          <>
            <div className={drawerLabelStyle}>Builds toward</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {buildsToward.map((q) => (
                <div
                  key={q.questId}
                  className={buildsTowardCardStyle}
                  onClick={() => onJumpToQuest(q.questId)}
                  data-testid={`detail-builds-toward-${q.questId}`}
                  role="button"
                >
                  <span
                    className="ic"
                    aria-hidden="true"
                    dangerouslySetInnerHTML={{ __html: questIconSvg(q.iconKey) }}
                  />
                  <span style={{ flex: 1 }}>{q.title}</span>
                  <span className="arrow">→</span>
                </div>
              ))}
            </div>
          </>
        )}

        <button
          type="button"
          className={drawerCtaStyle}
          onClick={onChoose}
          disabled={isStreaming}
          data-testid={`detail-start-${quest.questId}`}
        >
          Start this capability →
        </button>
      </div>
    </div>
  );
};
